import React, { useEffect, useState } from "react";
import { FaUserTag, FaUser } from "react-icons/fa";
import { RootDispatch, RootState } from "../../store";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import {
  addNavigation,
  disableUser,
  fetchUsers,
  getDisabledUsers,
  getRoleNavigationByRole,
} from "../../store/slices/userSlice";
import AddUserModal from "./AddUserModal";
import Button, { ButtonType } from "../../components/Assets/Button";
import ConfirmationModal from "../../components/ConfirmationModalComponent/ConfirmationModal";
import { getStatesData } from "../../store/slices/kanbanSlice";
import EditUserModal from "./EditUserModal";
import { IUser, Navigations, NavItem } from "../../models/userData.model";
import { fetchWarehouseNamesAndIdsThunk } from "../../store/slices/warehouseSlice";
import SearchInput from "../../components/Search/SearchInput";
import AddNavigationModal from "../../components/AddNavigationModal";
import UpdateRoleNavigationModal from "../../components/UpdateRoleNavigationModal";
import { EyeOff } from "lucide-react";

export interface NewUser {
  username: string;
  email: string;
  password: string;
  mobileNumber: string;
  states: string[];
  districts: string[];
  role: string;
  manager: string;
  isGeoLocationEnabled: boolean;
  trackLocation: boolean;
  warehouseAssigned: string;
  navigations: Navigations;
}

const UserManagement: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.get("search") ?? "";
  const activeTab =
    searchParams.get("status") === "disabled" ? "disabled" : "active";

  const updateViewParams = (updates: { status?: string; search?: string }) => {
    const nextParams = new URLSearchParams(searchParams);

    Object.entries(updates).forEach(([key, value]) => {
      if (value) {
        nextParams.set(key, value);
      } else {
        nextParams.delete(key);
      }
    });

    setSearchParams(nextParams, { replace: true });
  };

  const [newUser, setNewUser] = useState<NewUser>({
    username: "",
    email: "",
    password: "",
    mobileNumber: "",
    states: [] as string[],
    districts: [] as string[],
    role: "",
    manager: "",
    isGeoLocationEnabled: true,
    trackLocation: false,
    warehouseAssigned: "",
    navigations: {
      main: [],
      extras: [],
      frelit: [],
      notifications: null,
      customerNotifications: null,
    },
  });

  const dispatch: RootDispatch = useDispatch();
  const { user: users, disabledUsers } = useSelector(
    (state: RootState) => state.users,
  );
  const { warehouseNamesAndIds: warehouses } = useSelector(
    (state: RootState) => state.warehouse,
  );
  const statesData = useSelector((state: RootState) => state.kanban.statesData);

  const roles = [
    "admin",
    "sh",
    "bdm",
    "factory",
    "account",
    "bde",
    "telecaller",
    "distributor",
    "data entry",
    "sp",
    "hr",
    "LE",
    "CSE",
    "store",
    "floor",
    "digitalMarketing",
    "service",
    "dispatch-lv1",
    "dispatch-lv2",
  ];

  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [, setSelectedState] = useState("");
  const [, setSelectedDistrict] = useState("");
  const [isEditUserModalOpen, setIsEditUserModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<IUser | null>(null);
  const [userToDisable, setUserToDisable] = useState<IUser | null>(null);
  const [isDisablingUser, setIsDisablingUser] = useState(false);
  const [isAddNavigationOpen, setIsAddNavigationOpen] = useState(false);
  const [isUpdateRoleNavOpen, setIsUpdateRoleNavOpen] = useState(false);

  const getManagerOptions = () => {
    if (!users) return [];
    if (newUser.role === "bde" || newUser.role === "telecaller") {
      return users.filter((u: IUser) => u && u.role === "bdm");
    } else if (newUser.role === "bdm") {
      return users.filter((u: IUser) => u && u.role === "sh");
    } else if (newUser.role === "sh") {
      return users.filter((u: IUser) => u && u.role === "admin");
    }
    return users.filter((u: IUser) => u);
  };

  useEffect(() => {
    dispatch(fetchUsers());
  }, [dispatch]);

  useEffect(() => {
    if (activeTab === "disabled") {
      dispatch(getDisabledUsers());
    }
  }, [activeTab, dispatch]);

  useEffect(() => {
    if (statesData.length === 0) {
      dispatch(getStatesData());
    }
  }, [dispatch, statesData.length]);

  useEffect(() => {
    dispatch(fetchWarehouseNamesAndIdsThunk());
  }, [dispatch]);

  const usersForTab = activeTab === "active" ? users : disabledUsers;
  const filteredUsers = usersForTab?.filter((user: IUser) =>
    user?.username?.toLowerCase().includes(search?.toLowerCase()),
  );

  const handleAddNavigation = async (nav: NavItem) => {
    try {
      await dispatch(addNavigation(nav)).unwrap();
    } catch (err) {
      console.error("Failed to add navigation");
    }
  };

  const handleDisableUser = async () => {
    if (!userToDisable?._id) {
      return;
    }

    setIsDisablingUser(true);
    try {
      await dispatch(disableUser(userToDisable._id)).unwrap();
      setUserToDisable(null);
      await dispatch(fetchUsers());
      dispatch(getDisabledUsers());
    } catch (error) {
      console.error("Failed to disable user", error);
    } finally {
      setIsDisablingUser(false);
    }
  };

  const handleRoleChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const role = e.target.value;

    setSelectedState("");
    setSelectedDistrict("");

    setNewUser((prev) => ({
      ...prev,
      role,
      manager: "",
      states: [],
      districts: [],
      warehouseAssigned: "",
    }));

    try {
      const result = await dispatch(getRoleNavigationByRole(role)).unwrap();

      setNewUser((prev) => ({
        ...prev,
        navigations: {
          main: result?.main || [],
          extras: result?.extras || [],
          frelit: result?.frelit || [],
          notifications: result?.notifications || null,
          customerNotifications: result?.customerNotifications || null,
        },
      }));
    } catch (error) {
      console.error("Failed to fetch role navigation", error);
    }
  };

  const displayUserName = (role: string) => {
    if (role === "sh") {
      return "Sales Head";
    }
    if (role === "digitalMarketing") {
      return "Digital Marketing";
    }
    if (role === "bdm" || role === "bde" || role === "hr" || role === "LE") {
      return role.toUpperCase();
    }
    return role.charAt(0).toUpperCase() + role.slice(1).toLowerCase();
  };
  const FaUserIcon = FaUser as unknown as React.FC<
    React.SVGProps<SVGSVGElement>
  >;

  const FaUserTagIcon = FaUserTag as unknown as React.FC<
    React.SVGProps<SVGSVGElement>
  >;

  return (
    <div className="p-4 bg-gradient-to-r from-gray-50 to-gray-100 min-h-screen">
      <AddUserModal
        isOpen={isAddUserModalOpen}
        onClose={() => setIsAddUserModalOpen(false)}
        newUser={newUser}
        setNewUser={setNewUser}
        roles={roles}
        users={users as IUser[]}
        warehouses={warehouses}
        statesData={statesData}
        getManagerOptions={getManagerOptions}
        handleRoleChange={handleRoleChange}
      />
      <EditUserModal
        isOpen={isEditUserModalOpen}
        onClose={() => setIsEditUserModalOpen(false)}
        userId={selectedUserId}
        userData={selectedUser as IUser}
        statesData={statesData}
        roles={roles}
        users={users as IUser[]}
        onUserUpdated={() => {
          dispatch(fetchUsers());
        }}
      />
      <ConfirmationModal
        isOpen={userToDisable !== null}
        onClose={() => setUserToDisable(null)}
        onConfirm={handleDisableUser}
        title="Disable user?"
        message={`Are you sure you want to disable ${userToDisable?.username ?? "this user"}? They will no longer be able to access the system.`}
        confirmText="Disable user"
        type="danger"
        isLoading={isDisablingUser}
      />
      <div className="bg-white p-6 md:p-8 rounded-xl shadow-lg text-sm md:text-md">
        <div className="flex items-center gap-4 mb-8">
          <div className="relative w-96 shrink-0">
            <SearchInput
              placeholder="Search by username"
              value={search}
              className="w-full pr-4 py-2 bg-white border border-gray-200 rounded-md 
               focus:ring-1 focus:ring-navyBlue-primary focus:border-navyBlue-primary 
               outline-none transition-all text-sm text-gray-700 
               placeholder:text-gray-400 shadow-sm"
              onSearch={(term) => updateViewParams({ search: term })}
            />
          </div>
          <div className="flex gap-2">
            <Button
              type={ButtonType.Submit}
              onClick={() => setIsAddUserModalOpen(true)}
            >
              Add User
            </Button>
            <Button
              type={ButtonType.Submit}
              onClick={() => setIsAddNavigationOpen(true)}
            >
              Add Navigation
            </Button>
            <Button
              type={ButtonType.Submit}
              onClick={() => setIsUpdateRoleNavOpen(true)}
            >
              Update Role Access
            </Button>
          </div>
        </div>

        <div
          className="mb-5 flex border-b border-gray-200"
          role="tablist"
          aria-label="User status"
        >
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "active"}
            onClick={() => updateViewParams({ status: "active" })}
            className={`border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === "active"
                ? "border-navyBlue-primary text-navyBlue-primary"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            Active users ({users.length})
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "disabled"}
            onClick={() => updateViewParams({ status: "disabled" })}
            className={`border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === "disabled"
                ? "border-navyBlue-primary text-navyBlue-primary"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            Disabled users ({disabledUsers.length})
          </button>
        </div>

        {/* Users List */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {filteredUsers && filteredUsers.length > 0 ? (
            filteredUsers.map((user: IUser) => (
              <div
                key={user._id}
                className="bg-white p-4 rounded-lg shadow-md  border hover:shadow-lg transition duration-300 flex flex-col"
              >
                <div className="flex gap-2 items-center">
                  <FaUserIcon className="h-5 w-5 md:h-md:w-8 text-gray-300" />

                  {/* User Details */}
                  <div className="flex justify-between items-center w-full">
                    <div>
                      <h3 className="text-md  md:text-md font-semibold text-gray-800 ">
                        {user?.username}
                      </h3>
                      <p className="text-gray-600 text-xs ">
                        {user?.mobileNumber}
                      </p>
                      <p className="text-gray-600 text-xs md:text-sm ">
                        {user?.email}
                      </p>
                    </div>

                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() => setUserToDisable(user)}
                        aria-label={`Disable ${user.username}`}
                        title="Disable user"
                        className="text-gray-500 hover:text-red-600 transition-colors"
                      >
                        <EyeOff aria-hidden="true" size={16} />
                      </button>
                      <svg
                        onClick={() => {
                          setSelectedUserId(user._id ?? "");
                          setSelectedUser(user);
                          setIsEditUserModalOpen(true);
                        }}
                        className="cursor-pointer flex justify-self-end hover:scale-95 duration-300"
                        width="16px"
                        height="16px"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          fill="#1C274C"
                          d="M11.9426 1.25L13.5 1.25C13.9142 1.25 14.25 1.58579 14.25 2C14.25 2.41421 13.9142 2.75 13.5 2.75H12C9.62178 2.75 7.91356 2.75159 6.61358 2.92637C5.33517 3.09825 4.56445 3.42514 3.9948 3.9948C3.42514 4.56445 3.09825 5.33517 2.92637 6.61358C2.75159 7.91356 2.75 9.62178 2.75 12C2.75 14.3782 2.75159 16.0864 2.92637 17.3864C3.09825 18.6648 3.42514 19.4355 3.9948 20.0052C4.56445 20.5749 5.33517 20.9018 6.61358 21.0736C7.91356 21.2484 9.62178 21.25 12 21.25C14.3782 21.25 16.0864 21.2484 17.3864 21.0736C18.6648 20.9018 19.4355 20.5749 20.0052 20.0052C20.5749 19.4355 20.9018 18.6648 21.0736 17.3864C21.2484 16.0864 21.25 14.3782 21.25 12V10.5C21.25 10.0858 21.5858 9.75 22 9.75C22.4142 9.75 22.75 10.0858 22.75 10.5V12.0574C22.75 14.3658 22.75 16.1748 22.5603 17.5863C22.366 19.031 21.9607 20.1711 21.0659 21.0659C20.1711 21.9607 19.031 22.366 17.5863 22.5603C16.1748 22.75 14.3658 22.75 12.0574 22.75H11.9426C9.63423 22.75 7.82519 22.75 6.41371 22.5603C4.96897 22.366 3.82895 21.9607 2.93414 21.0659C2.03933 20.1711 1.63399 19.031 1.43975 17.5863C1.24998 16.1748 1.24999 14.3658 1.25 12.0574V11.9426C1.24999 9.63423 1.24998 7.82519 1.43975 6.41371C1.63399 4.96897 2.03933 3.82895 2.93414 2.93414C3.82895 2.03933 4.96897 1.63399 6.41371 1.43975C7.82519 1.24998 9.63423 1.24999 11.9426 1.25ZM16.7705 2.27592C18.1384 0.908029 20.3562 0.908029 21.7241 2.27592C23.092 3.6438 23.092 5.86158 21.7241 7.22947L15.076 13.8776C14.7047 14.2489 14.4721 14.4815 14.2126 14.684C13.9069 14.9224 13.5761 15.1268 13.2261 15.2936C12.929 15.4352 12.6169 15.5392 12.1188 15.7052L9.21426 16.6734C8.67801 16.8521 8.0868 16.7126 7.68711 16.3129C7.28742 15.9132 7.14785 15.322 7.3266 14.7857L8.29477 11.8812C8.46079 11.3831 8.56479 11.071 8.7064 10.7739C8.87319 10.4239 9.07761 10.0931 9.31605 9.78742C9.51849 9.52787 9.7511 9.29529 10.1224 8.924L16.7705 2.27592ZM20.6634 3.33658C19.8813 2.55448 18.6133 2.55448 17.8312 3.33658L17.4546 3.7132C17.4773 3.80906 17.509 3.92327 17.5532 4.05066C17.6965 4.46372 17.9677 5.00771 18.48 5.51999C18.9923 6.03227 19.5363 6.30346 19.9493 6.44677C20.0767 6.49097 20.1909 6.52273 20.2868 6.54543L20.6634 6.16881C21.4455 5.38671 21.4455 4.11867 20.6634 3.33658ZM19.1051 7.72709C18.5892 7.50519 17.9882 7.14946 17.4193 6.58065C16.8505 6.01185 16.4948 5.41082 16.2729 4.89486L11.2175 9.95026C10.801 10.3668 10.6376 10.532 10.4988 10.7099C10.3274 10.9297 10.1804 11.1676 10.0605 11.4192C9.96337 11.623 9.88868 11.8429 9.7024 12.4017L9.27051 13.6974L10.3026 14.7295L11.5983 14.2976C12.1571 14.1113 12.377 14.0366 12.5808 13.9395C12.8324 13.8196 13.0703 13.6726 13.2901 13.5012C13.468 13.3624 13.6332 13.199 14.0497 12.7825L19.1051 7.72709Z"
                        />
                      </svg>
                    </div>
                  </div>
                </div>

                <div className="flex justify-between gap-2 mt-2">
                  <div className="text-xs md:text-base text-gray-600">
                    <FaUserTagIcon className="inline-block mr-1" />
                    {displayUserName(user?.role)}
                  </div>
                  <div>
                    {user.manager && (
                      <p className="text-gray-600 text-xs md:text-sm">
                        <span className="font-semibold text-xs">Manager:</span>{" "}
                        {typeof user.manager === "string"
                          ? users?.find((u: IUser) => u._id === user.manager)
                              ?.username
                          : user.manager &&
                              typeof user.manager === "object" &&
                              "username" in user.manager
                            ? (user.manager as { username?: string }).username
                            : ""}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <p className="text-center text-gray-600 col-span-full">
              No users found
            </p>
          )}
        </div>
      </div>
      {/* <NavigationAccessModal
        isOpen={isAccessModalOpen}
        onClose={() => setIsAccessModalOpen(false)}
        navigations={newUser.navigations}
        setNavigations={(nav) =>
          setNewUser((prev) => ({
            ...prev,
            navigations: nav,
          }))
        }
      /> */}
      <AddNavigationModal
        isOpen={isAddNavigationOpen}
        onClose={() => setIsAddNavigationOpen(false)}
        onSubmit={handleAddNavigation}
      />
      <UpdateRoleNavigationModal
        isOpen={isUpdateRoleNavOpen}
        onClose={() => setIsUpdateRoleNavOpen(false)}
        roles={roles}
      />
      ;
    </div>
  );
};

export default UserManagement;
